import { useRef, type ChangeEvent } from "react";

import type { DocumentWorkflowFile } from "../api/generated";
import { useDocumentFiles } from "./documentFilesContext";
import { displayDateTime } from "./fileLabels";
import { useNewVersionUpload } from "./useNewVersionUpload";
import { Notice } from "./Notice";

/**
 * Загрузка новой версии файла с защитой от параллельного изменения.
 *
 * Новая версия основана на той, что человек видел, нажимая кнопку. Если за
 * время загрузки появилась другая, сервер отвечает конфликтом, и он
 * показывается отдельным состоянием — со способом восстановиться, а не
 * общей ошибкой.
 */
export function NewVersionUpload({ file }: { file: DocumentWorkflowFile }) {
  const { services, accessScopeId, authorName } = useDocumentFiles();
  const inputRef = useRef<HTMLInputElement>(null);
  const { state, begin, upload, refreshHistory, retryOnLatest, dismiss } =
    useNewVersionUpload({ services, fileId: file.id, accessScopeId });
  const current = file.current_version;
  const busy = state.phase === "uploading";

  if (current === null) return null;

  const select = (event: ChangeEvent<HTMLInputElement>) => {
    const [selected] = event.target.files ?? [];
    event.target.value = "";
    if (selected) void upload(selected, current.id);
  };

  return (
    <div className="new-version">
      <button
        className="button button--secondary button--compact"
        type="button"
        disabled={busy}
        onClick={() => {
          begin(current.id);
          inputRef.current?.click();
        }}
      >
        {busy ? "Загружаем версию…" : "Загрузить новую версию"}
      </button>
      <input
        ref={inputRef}
        className="visually-hidden"
        aria-label={`Новая версия файла ${file.display_name}`}
        type="file"
        onChange={select}
      />
      {busy ? (
        <p role="status" aria-busy="true" className="dw-secondary">
          {state.progress === null || state.progress >= 1
            ? "Загружаем и проверяем файл…"
            : `Загружаем… ${Math.round(state.progress * 100)}%`}
        </p>
      ) : null}
      {state.phase === "created" ? (
        <Notice role="status">
          Создана версия {state.version.version_number}. Предыдущие версии
          остались без изменений.{" "}
          <button
            className="button button--text button--compact"
            type="button"
            onClick={dismiss}
          >
            Скрыть
          </button>
        </Notice>
      ) : null}
      {state.phase === "failed" ? (
        <Notice>
          {state.message}{" "}
          <button
            className="button button--text button--compact"
            type="button"
            onClick={dismiss}
          >
            Скрыть
          </button>
        </Notice>
      ) : null}
      {state.phase === "conflict" ? (
        <div
          className="new-version__conflict"
          role="alert"
          data-conflict="base-version"
        >
          <p>
            <strong>Файл изменили, пока вы загружали версию.</strong> У «
            {file.display_name}» появилась другая версия, поэтому ваша версия не
            создана — иначе чужие изменения были бы затёрты. Это не сбой сети:
            загруженный файл сохранён, и ничего не потеряно.
          </p>
          {state.refreshed ? (
            state.latest ? (
              <p>
                Сейчас актуальна версия {state.latest.version_number} от{" "}
                {displayDateTime(state.latest.created_at)}, автор:{" "}
                {authorName(state.latest.created_by)}. Посмотрите изменения в
                истории версий и решите, создавать ли версию «
                {state.pending.fileName}» поверх неё.
              </p>
            ) : (
              <p>У файла не осталось версий: создать новую версию нельзя.</p>
            )
          ) : null}
          {state.refreshFailed ? (
            <Notice>Не удалось обновить историю. Повторите попытку.</Notice>
          ) : null}
          <div className="inline-actions">
            {state.refreshed && state.latest ? (
              <button
                className="button button--primary button--compact"
                type="button"
                onClick={() => void retryOnLatest()}
              >
                Создать версию поверх актуальной
              </button>
            ) : (
              <button
                className="button button--primary button--compact"
                type="button"
                disabled={state.refreshing}
                onClick={() => void refreshHistory()}
              >
                {state.refreshing
                  ? "Обновляем историю…"
                  : "Обновить историю версий"}
              </button>
            )}
            <button
              className="button button--text button--compact"
              type="button"
              onClick={dismiss}
            >
              Отменить
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
